import { type FormEvent, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CreateCarrierInviteVars,
  useCarrierDispatchCenters,
  useCreateCarrierInvite,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";

interface InviteTeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allowedCarrierIds: readonly string[];
}

const ROLE_OPTIONS = [
  { value: "CARRIER_DISPATCHER", label: "Dispatcher" },
  { value: "CARRIER_OWNER", label: "Carrier Admin" },
] as const;

const ALL_CENTERS_VALUE = "__all__";

export function InviteTeamMemberDialog({
  open,
  onOpenChange,
  allowedCarrierIds,
}: InviteTeamMemberDialogProps) {
  const dispatchCenters = useCarrierDispatchCenters();
  const create = useCreateCarrierInvite();
  const [email, setEmail] = useState("");
  const [role, setRole] =
    useState<CreateCarrierInviteVars["role"]>("CARRIER_DISPATCHER");
  const [dispatchCenterId, setDispatchCenterId] =
    useState<string>(ALL_CENTERS_VALUE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submissionLock = useRef(false);

  const centers = useMemo(
    () =>
      (dispatchCenters.data ?? [])
        .filter(
          (center) =>
            center.isActive &&
            allowedCarrierIds.includes(center.pickupProviderCarrierId)
        )
        .map((center) => ({ id: center.id, label: center.name })),
    [dispatchCenters.data, allowedCarrierIds]
  );
  const centersForbidden = hasHttpStatus(dispatchCenters.error, 403);
  const accessOptionsUnavailable =
    (dispatchCenterId !== ALL_CENTERS_VALUE &&
      !centers.some((center) => center.id === dispatchCenterId)) ||
    centersForbidden ||
    ((dispatchCenters.isLoading || !!dispatchCenters.error) &&
      centers.length === 0);
  const formId = "invite-team-member-form";

  const reset = () => {
    setEmail("");
    setRole("CARRIER_DISPATCHER");
    setDispatchCenterId(ALL_CENTERS_VALUE);
    setError(null);
  };

  const close = () => {
    if (saving) return;
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submissionLock.current) return;
    const normalizedEmail = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    if (accessOptionsUnavailable) {
      setError(
        "You cannot send an invitation until access options load. Try again."
      );
      return;
    }

    submissionLock.current = true;
    setSaving(true);
    setError(null);
    try {
      await create.mutateAsync({
        email: normalizedEmail,
        role,
        dispatchCenterId:
          dispatchCenterId === ALL_CENTERS_VALUE ? null : dispatchCenterId,
      });
      toast.success("Team invitation sent.");
      reset();
      onOpenChange(false);
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to invite team members."
        : (response.response?.data?.error ??
          "Team invitation could not be sent.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <DetailDrawer
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      title="Invite team member"
      description="Choose the role and dispatch-center access this person should receive."
      closeDisabled={saving}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="touch"
            className="w-full sm:w-auto"
            onClick={close}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            size="touch"
            className="w-full sm:w-auto"
            disabled={saving || accessOptionsUnavailable}
          >
            {saving ? "Sending…" : "Send invite"}
          </Button>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-busy={saving || undefined}
      >
        <Field label="Email" id="invite-email">
          <Input
            id="invite-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            disabled={saving}
            onChange={(event) => {
              setEmail(event.target.value);
              setError(null);
            }}
          />
        </Field>

        <Field label="Role" id="invite-role">
          <NativeSelect
            id="invite-role"
            density="compact"
            value={role}
            disabled={saving}
            onChange={(event) => {
              setRole(event.target.value as CreateCarrierInviteVars["role"]);
              setError(null);
            }}
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        </Field>

        <Field label="Dispatch center scope" id="invite-center">
          <NativeSelect
            id="invite-center"
            density="compact"
            value={dispatchCenterId}
            disabled={saving || accessOptionsUnavailable}
            onChange={(event) => {
              setDispatchCenterId(event.target.value);
              setError(null);
            }}
          >
            <option value={ALL_CENTERS_VALUE}>
              All centers (carrier-wide)
            </option>
            {centers.map((center) => (
              <option key={center.id} value={center.id}>
                {center.label}
              </option>
            ))}
          </NativeSelect>
        </Field>

        {dispatchCenters.isLoading && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading dispatch-center access options…
          </p>
        )}
        {dispatchCenters.error && (
          <p
            role="alert"
            className={
              centers.length > 0
                ? "text-sm text-amber-800"
                : "text-sm text-amber-800"
            }
          >
            {centersForbidden
              ? "You do not have permission to load dispatch-center options. You cannot send an invitation until access options load."
              : centers.length > 0
                ? "Dispatch-center access options could not be refreshed. Loaded options remain available."
                : "Dispatch-center options could not be loaded. You cannot send an invitation until access options load."}
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {saving && (
          <p role="status" className="text-sm text-muted-foreground">
            Sending team invitation…
          </p>
        )}
      </form>
    </DetailDrawer>
  );
}

function Field({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}
